import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { KEY_RECOIL, TYPE_BEAT_ENUM } from '@/shared/config';
import {
  getEventEffectdByUpdatedEvent,
  getMultipleEventEffectdByUpdatedEvent,
} from '@/entities/event';
import {
  BEAT_HR_TAB_ID,
  DEFAULT_CONTROL_MODAL_VALUE,
  selectControlModal,
  selectEventChanges,
  selectEventNews,
  selectHolterEvents,
  selectIsLoadingStrip,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import { updateMultipleStripType, updateStripType } from '@/entities/beat';

/**
 * Backs the beats tab event type toolbar — delete, change duration and reset — over the single or multiple strip selection. Resolves the effective selected event by preferring a pending change over the loaded Holter event, derives the disabled and reset-visible flags from saving state, empty selection, multi-selection and deleted-type changes, applies type updates through the undo/redo aware event options, and binds the D and C shortcuts while the tab is hovered.
 *
 * @returns An object with the strip loading flag, the disabled flags for the duration, changing and common buttons, the control modal getter and setter, the strip type change handler, whether the reset button shows, and the reset handler.
 */
const useButtonTypes = () => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;

  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the beats tab pending event changes into the report store. Forwards the value to the shared setEventChanges action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes the beats tab modal control flags into the report store. Forwards the value to the shared setControlModal action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  /**
   * Reads the beats tab modal control flags from the report store without subscribing. Applies the tab-keyed control modal selector to the current store snapshot, so click and key handlers see the flags as they are right now.
   *
   * @returns The current control modal flag set for the beats tab.
   */
  const getControlModal = () => selectControlModal(keyRecoil)(useReportStore.getState());
  const controlModal = useReportStore(selectControlModal(keyRecoil));
  /**
   * Reads the unsaved new events of the beats tab from the report store snapshot. Applies the tab-keyed event news selector without subscribing, so reset and change handlers work against the latest array.
   *
   * @returns The current list of newly added events on the beats tab.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Writes the beats tab list of newly added events into the report store. Forwards the value to the shared setEventNews action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  /**
   * Reads the loaded Holter events of the beats tab from the report store snapshot. Applies the tab-keyed Holter events selector without subscribing, so the reset handler can look up an event's original server state.
   *
   * @returns The current Holter event list for the beats tab.
   */
  const getHolterEvents = () => selectHolterEvents(keyRecoil)(useReportStore.getState());
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the beats tab event options snapshot that undo/redo replays into the report store. Forwards the value to the shared setEventOptions action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const holterEvents = useReportStore(selectHolterEvents(keyRecoil));
  const eventNews = useReportStore(selectEventNews(keyRecoil));

  // selectedStripState (Recoil/index.ts, Task 7) is typed IEvent | IBeat | IEcgBookmark; this file
  // reads/writes event-only fields (idEvent, isNew, originalData, ...) that IBeat/IEcgBookmark lack - pre-existing gap.
  const sletedEvent: any = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Writes the currently selected beats tab strip into the report store. Forwards the value to the shared setSelectedStrip action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const selectedStrip: any = useMemo(() => {
    const foundEventChange = _.find(eventChanges, (x) => x?.idEvent === sletedEvent?.idEvent);
    if (foundEventChange) {
      return foundEventChange;
    }
    const foundEvent = holterEvents.find((x) => x.idEvent === sletedEvent?.idEvent);
    if (foundEvent) {
      return foundEvent;
    }
    return { ...(sletedEvent || {}), ...(sletedEvent?.originalData || {}) };
  }, [sletedEvent, eventChanges, holterEvents]);
  const mergeSelectedStrip = _.filter(
    _.uniqBy([selectedStrip || {}, ...(selectedMultipleStrip || [])], 'idEvent'),
    (x) => !_.isEmpty(x),
  );
  const isSelectNewEvent = selectedStrip?.isNew;
  const foundEventChange: any = useMemo(() => {
    const { event } = getEventEffectdByUpdatedEvent(selectedStrip, eventNews, eventChanges);
    return event;
  }, [eventChanges, selectedStrip, eventNews]);
  const isChangeToDeletedEvent = foundEventChange?.type === TYPE_BEAT_ENUM.DELETED;
  const foundHasChanged = _.find(eventChanges, (x) => x?.idEvent === selectedStrip?.idEvent);
  /**
   * Signals that the event type buttons must stay disabled because a save is in flight for the current selection. Resolves truthy when the pending change for the selected strip is marked as saving, or when any strip of the multi-selection has a matching event change being saved.
   *
   * @returns The saving flag of the selected strip's pending change, or the first multi-selected strip whose change is saving, otherwise falsy.
   */
  const isDisableWhenSaving =
    foundHasChanged?.isSaving ||
    selectedMultipleStrip.find(
      (x) => eventChanges.find((d) => d?.idEvent === x?.idEvent)?.isSaving,
    );
  const isNotSelectedStrip = _.isEmpty(mergeSelectedStrip) && !selectedStrip?.idEvent;
  const isSelectedMultipleStrip = !_.isEmpty(mergeSelectedStrip) && mergeSelectedStrip.length > 1;
  const isDisableButton = isNotSelectedStrip || isDisableWhenSaving || isLoadingStrip;
  const isDisableCommonButton = isDisableButton || isDisableWhenSaving;
  const isDisableChangeDuration =
    isSelectedMultipleStrip || isChangeToDeletedEvent || isSelectNewEvent || isDisableButton;
  const isDisableChangingButton = isDisableButton || controlModal.isShowChangeDurationButtonType;
  const isShowResetButton = useMemo(() => {
    if (foundHasChanged) return true;
    if (foundEventChange) return true;
    if (mergeSelectedStrip.length > 0) {
      const isChangedBulk = false;

      return (
        isChangedBulk ||
        !!mergeSelectedStrip.find((event: any) => {
          const { event: deleteEvent } = getEventEffectdByUpdatedEvent(
            event,
            eventNews,
            eventChanges,
          );
          if (deleteEvent) {
            return true;
          }
          const foundChange = _.find(eventChanges, (x) => x?.idEvent === event?.idEvent);
          if (foundChange) {
            return true;
          }
          return false;
        })
      );
    }
    return false;
  }, [
    foundHasChanged,
    selectedMultipleStrip,
    eventChanges,
    mergeSelectedStrip,
    foundEventChange,
    eventNews,
  ]);

  /**
   * Applies an event type change to the selected beat strip, or to every strip of the multi-selection. Uses the passed type or falls back to the selection's own type, routes through the multiple or single strip type updater against the current event changes, mirrors the result into the event options so undo/redo can group the action, and commits it as the new event changes.
   *
   * @param type - Target event type, or falsy to reuse the selected strip's current type.
   * @returns Nothing.
   */
  const handleChangeStripType = (type: any) => {
    let stripChangesUpdate: any;
    const updateType = type || selectedStrip?.type;
    if (selectedMultipleStrip.length > 0) {
      stripChangesUpdate = updateMultipleStripType({
        stripChanges: eventChanges,
        strips: mergeSelectedStrip,
        type: updateType,
      });
    } else {
      stripChangesUpdate = updateStripType({
        stripChanges: eventChanges,
        strip: selectedStrip,
        type: updateType,
      });
    }

    // Perform grouping event action applicable to undo/redo
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventChangesState: stripChangesUpdate,
    }));
    setEventChanges(stripChangesUpdate);
  };

  /**
   * Discards every pending change tied to the current beats tab selection, including the events linked to it by overlap. Filters the selected and multi-selected ids out of the event changes, removes the new events and changes linked to them through the overlap lookup and each reset event's own link list, re-points the selection at the original Holter event, and writes both arrays back through the event options for undo/redo.
   *
   * @returns Nothing.
   */
  const onClickResetChange = () => {
    const resetEvents: any[] = [];
    const clonedEventNews = _.cloneDeep(getEventNews());

    //* Remove những event đang được select khỏi mảng event changes
    const newEventChanges = eventChanges.filter((x) => {
      if (x.idEvent !== selectedStrip?.idEvent) {
        const foundEventInMultipleSelected = selectedMultipleStrip.find(
          (strip: any) => strip.idEvent === x.idEvent,
        );
        if (foundEventInMultipleSelected) {
          resetEvents.push(x);
          return false;
        }
        return true;
      }
      resetEvents.push(x);
      return false;
    });
    const linkEvents = getMultipleEventEffectdByUpdatedEvent(
      mergeSelectedStrip,
      eventNews,
      eventChanges,
    );

    // linkEventNew/linkEventChange (handler.ts, Task 7) are typed IEvent | {}, so .id/.idEvent
    // don't exist on the {} branch - pre-existing.
    _.forEach(linkEvents, (linkEvent: any) => {
      _.remove(clonedEventNews, (x) => {
        if (x.id === linkEvent?.linkEventNew?.id) {
          return true;
        }
        return false;
      });
      _.remove(newEventChanges, (x) => {
        if (x.idEvent === linkEvent?.linkEventChange?.idEvent) {
          return true;
        }
        return false;
      });
    });
    //* Check những event bị remove
    //* New event chắc chắn có link event nếu overlap còn event change thì tùy event bị change duration mới có link event nếu overlap
    resetEvents.forEach((resetEvent: any) => {
      //* Remove những event new / change liên quan tới event bị remove
      //* Trong new event sẽ check những event bị reset nếu liên quan tới new event thì new event đó bị remove
      _.remove(clonedEventNews, (eventNew) => {
        const { linkEvents } = eventNew;
        if (
          linkEvents?.length &&
          linkEvents.find((linkEvent) => linkEvent.idEvent === resetEvent.idEvent)
        ) {
          _.remove(newEventChanges, (x) =>
            linkEvents.find((linkEvent) => linkEvent.idEvent === x.idEvent),
          );
          return true;
        }
        return false;
      });
      //* Trong event change sẽ check nếu có event liên quan tới event bị reset thì remove event change đó
      if (resetEvent.linkEvents?.length) {
        _.remove(newEventChanges, (x) =>
          resetEvent.linkEvents.find((linkEvent: any) => linkEvent.idEvent === x.idEvent),
        );
      } else {
        eventChanges.forEach((eventChange) => {
          const { linkEvents } = eventChange;
          if (linkEvents?.length && linkEvents.find((x) => x.idEvent === resetEvent.idEvent)) {
            _.remove(
              newEventChanges,
              (x) =>
                linkEvents.find((linkEvent: any) => linkEvent.idEvent === x.idEvent) ||
                x.idEvent === eventChange.idEvent,
            );
          }
        });
      }
    });
    if (clonedEventNews.length !== getEventNews().length) {
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventNewsState: clonedEventNews,
      }));
      setEventNews(clonedEventNews);
    }

    setSelectedStrip(getHolterEvents().find((x) => x.idEvent === selectedStrip?.idEvent) || null);
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventChangesState: newEventChanges,
    }));
    setEventChanges(newEventChanges);
  };

  useEffect(() => {
    /**
     * Runs the beats tab type toolbar keyboard shortcuts from a window keydown. Bails on auto-repeat, while strips are loading, when the beats tab zone is not hovered, or on any Ctrl or Meta combination, then maps key code 68 (D) to a delete-type change and key code 67 (C) to opening the change duration modal, each respecting its own disabled flag.
     *
     * @param event - Keydown event carrying the key code and the modifier flags.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (event?.repeat || isLoadingStrip) {
        return;
      }
      const tabZone = document.getElementById(BEAT_HR_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }
      switch (keyCode) {
        case 68: {
          // Delete (D)
          if (!isDisableChangingButton) {
            handleChangeStripType(TYPE_BEAT_ENUM.DELETED);
          }
          break;
        }
        case 67: {
          // Change duration C
          if (!isDisableChangeDuration && !getControlModal().isShowChangeDurationButtonType) {
            setControlModal({
              ...DEFAULT_CONTROL_MODAL_VALUE,
              isShowChangeDurationButtonType: true,
            });
          }
          break;
        }
        default:
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // Dependency use in handleChangeStripType & isDisableButton func
  }, [selectedMultipleStrip, selectedStrip, eventChanges, isLoadingStrip, isDisableChangingButton]);

  return {
    isLoadingStrip,
    isDisableChangeDuration,
    getControlModal,
    setControlModal,
    handleChangeStripType,
    isDisableChangingButton,
    isShowResetButton,
    isDisableCommonButton,
    onClickResetChange,
  };
};

export default useButtonTypes;
